import React, { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getDeliveryHours,
  getSpecialEventsReasons,
  set12HFormat,
} from './../../redux/actions/newOperationHoursActions';
import {
  getRestaurantServices,
  setRestaurantService,
  setSelectedRestaurantList as setSelectedRestaurants,
} from './../../redux/actions/restaurantsActions';
import {
  AppBar,
  Box,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import SettingsOption from './SettingsOptions';
import BaseAdjustment from './BaseAdjustment';
import SingleDaySpecialEvent from './SingleDaySpecialEvent';
import RangeDaySpecialEvent from './RangeDaySpecialEvent';
import { servicesPermission } from './constants/OperationHoursPermission';
import { getHourCycle } from '../../utils/utils';
import RBISystemDownModal from '../RBIMessagePopup/RBISystemDownModal';
import RBISuccessPopup from '../RBIMessagePopup/RBISuccessPopup';
import { getHealthCheckMDS } from '../../redux/actions/newOperationHoursActions';
import SupplyIntakeDays from './SupplyIntakeDays';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    tableCheckbox: {
      [theme.breakpoints.between('sm', 'md')]: {
        display: 'flex',
        flexDirection: 'row',
        paddingLeft: 2,
        marginLeft: 2,
      },
      [theme.breakpoints.between('md', 'lg')]: {
        display: 'flex',
        flexDirection: 'row',
        paddingLeft: 2,
        marginLeft: 2,
      },
    },
    desktopCheckbox: {
      [theme.breakpoints.between('lg', 'xl')]: {
        display: 'flex',
        flexDirection: 'row',
        paddingLeft: 2.8,
        marginLeft: 0,
      },
    },
    desktopCheckboxControl: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    },
    tabletWeekdaysSelection: {
      display: 'flex',
      justifyContent: 'flex-start',
      margin: 'auto',
      [theme.breakpoints.between('sm', 'md')]: {
        display: 'flex',
        flexDirection: 'row',
      },
    },
    desktopWeekdaysSelection: {
      display: 'flex',
      justifyContent: 'flex-start',
      margin: 'auto',
      [theme.breakpoints.between('lg', 'xl')]: {
        display: 'flex',
        flexDirection: 'row',
      },
    },
    paper: {
      padding: 0,
      textAlign: 'left',
      color: theme.palette.text.secondary,
      height: '60vh',
      overflow: 'auto',
    },
    paper2: {
      padding: theme.spacing(2),
      textAlign: 'left',
      color: theme.palette.text.secondary,
      height: '60vh',
      overflow: 'auto',
    },
    toolbar: {
      minHeight: 50,
    },
    paperCalendar: {
      textAlign: 'left',
      height: '60vh',
      overflow: 'hidden',
    },
    calendarFooter: {
      float: 'right',
    },
    holydayTypography: {
      marginTop: '1%',
      marginLeft: '5%',
      color: 'black',
    },
    holydaySwtich: {
      marginLeft: '25%',
    },
    appBar: {
      backgroundColor: 'rgb(138, 138, 138)',
    },
  }),
);

function OperationHoursTab() {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch: any = useDispatch();

  const [selectedOption, setSelectedOption] = useState<number>(0);

  const brand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );

  const { RestaurantId, CountryId, RestaurantStatus } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  );

  const isRestaurantClosed =
    RestaurantId && ['Closed', 'Temp Closed'].includes(RestaurantStatus);

  const { is12HFormat, isMDSUpAndRunning } = useSelector(
    (state: RootStateOrAny) => state.NewOperationHoursReducer,
  );

  const [showSystemDownModal, setShowSystemDownModal] =
    useState<boolean>(false);

  const [showRestaurantCloseModal, setShowRestaurantCloseModal] =
    useState<boolean>(false);

  useEffect(() => {
    const isSystemDown = !isMDSUpAndRunning;
    setShowSystemDownModal(isSystemDown);
    setShowRestaurantCloseModal(!isSystemDown && isRestaurantClosed);
  }, [isMDSUpAndRunning, selectedOption, isRestaurantClosed]);

  function closeRestaurantCloseModal() {
    setShowRestaurantCloseModal(false);
  }

  function closeSystemDownModal() {
    setShowSystemDownModal(false);
  }

  useEffect(() => {
    dispatch(getHealthCheckMDS());
    dispatch(getSpecialEventsReasons());
  }, [selectedOption]);

  useEffect(() => {
    dispatch(set12HFormat({ H12Format: getHourCycle() }));
    return () => {
      if (!RestaurantId) dispatch(setSelectedRestaurants([]));
    };
  }, []);

  useEffect(() => {
    if (RestaurantId) {
      dispatch(getRestaurantServices(RestaurantId));
    } else {
      dispatch(setRestaurantService(servicesPermission));
    }
  }, [RestaurantId]);

  useEffect(() => {
    if (brand && CountryId) {
      dispatch(getDeliveryHours(brand, CountryId));
    }
  }, [brand, CountryId]);

  const getSelectedForm = () => {
    switch (selectedOption) {
      case 1:
        return <RangeDaySpecialEvent />;
      case 2:
        return <SingleDaySpecialEvent />;
      case 3:
        return <SupplyIntakeDays classes={classes} />;
      case 0:
      default:
        return <BaseAdjustment classes={classes} />;
    }
  };

  return (
    <div>
      <Grid container xs={12} spacing={2}>
        <Grid item xs={3}>
          <AppBar position="static" className={classes.appBar}>
            <Toolbar variant="dense">
              <Typography>{t('operationHours.selectScheduleType')}</Typography>
            </Toolbar>
          </AppBar>
          <Paper className={classes.paper}>
            <SettingsOption
              selectedOption={selectedOption}
              setOption={setSelectedOption}
            />
          </Paper>
        </Grid>
        {getSelectedForm()}
      </Grid>
      {!RestaurantId && (
        <ListItemText
          primary={`++ ${t('operationHours.serviceModeOptionalMessage')}`}
          primaryTypographyProps={{
            style: { fontSize: '12px', fontStyle: 'italic' },
          }}
        />
      )}
      {showSystemDownModal && (
        <RBISystemDownModal
          isOpen={showSystemDownModal}
          onClose={closeSystemDownModal}
        />
      )}
      {showRestaurantCloseModal && (
        <RBISuccessPopup
          isOpen={showRestaurantCloseModal}
          isAlert={true}
          closeModal={closeRestaurantCloseModal}
          message={t('operationHours.restaurantClosedMessage')}
          messageHeader={t('operationHours.error.headerText')}
        />
      )}

      {/* Time Format Controls - Moved to bottom left of page */}
      {selectedOption !== 3 && (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            marginTop: '20px',
            padding: '10px',
          }}
        >
          <Typography style={{ marginRight: 10, fontSize: 15 }}>
            {t('operationHours.timeFormatCheckbox')}
          </Typography>
          <FormControl style={{ minWidth: 140 }}>
            <Select
              onChange={e =>
                dispatch(
                  set12HFormat({
                    H12Format: JSON.parse(e.target.value as string),
                  }),
                )
              }
              value={JSON.stringify(is12HFormat)}
              style={{ fontSize: 15 }}
              label={t('operationHours.timeFormatCheckbox')}
            >
              <MenuItem key="operationHours.12HourLabel" value={'true'}>
                {t('operationHours.12HourLabel')}
              </MenuItem>
              <MenuItem key="operationHours.24Hour" value={'false'}>
                {t('operationHours.24Hour')}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}
    </div>
  );
}

export default OperationHoursTab;
